import {
  type FormEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router";

import {
  ArrowUpRight,
  Ban,
  CheckCircle2,
  ChevronRight,
  Clipboard,
  Loader2,
} from "lucide-react";

import { GcsImage } from "@/components/media/gcs-image";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { isEnrichmentContentType } from "@/lib/tina/content-types";
import {
  enrichmentEntityLabel,
  enrichmentProblemOf,
} from "@/lib/tina/enrichment-jobs";
import { asRecord } from "@/lib/tina/value-readers";
import { cn } from "@/lib/utils";
import { KeyValueGrid } from "@/pages/tina/items/components/key-value-grid";
import { PanelShell } from "@/pages/tina/items/components/panel-shell";
import { coalesceCopy } from "@/pages/tina/items/components/panels/hitl-panel/coalescing";
import { curatorReviewProblemOf } from "@/pages/tina/items/components/panels/hitl-panel/curator-review-resolution";
import { EnrichmentContextView } from "@/pages/tina/items/components/panels/hitl-panel/enrichment-context";
import { buildTinaHitlEventPrefill } from "@/pages/tina/items/components/panels/hitl-panel/hitl-event-prefill";
import type { TinaHitlEventPrefillPayload } from "@/pages/tina/items/components/panels/hitl-panel/hitl-event-prefill";
import {
  canCreateMoodEventFromHitl,
  readCinemaScreeningContext,
  readMatchedArtists,
  readMatchedVenue,
  readMusicEventContext,
  readSportsMatchContext,
  readTheaterPerformanceContext,
} from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";
import type {
  CinemaScreeningContext,
  MusicEventContext,
  SportsMatchContext,
  TextRef,
  TheaterPerformanceContext,
} from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";
import { hitlQueueReturnTo } from "@/pages/tina/items/components/panels/hitl-panel/queue-return";
import { RefreshContextView } from "@/pages/tina/items/components/panels/hitl-panel/refresh-context";
import { resolutionComponentFor } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { ScoreBreakdownView } from "@/pages/tina/items/components/score-breakdown";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import { shortId } from "@/pages/tina/items/item-context";
import type {
  EnrichmentLowConfidenceProblem,
  HitlContext,
  HitlProblem,
  PipelineItem,
} from "@/types/tina";

const CREATE_HREF = "/events/create?populate=clipboard";

interface DerivedHitlData {
  musicContext: MusicEventContext | null;
  cinemaContext: CinemaScreeningContext | null;
  theaterContext: TheaterPerformanceContext | null;
  sportsContext: SportsMatchContext | null;
  matchedVenue: TextRef | null;
  matchedArtists: TextRef[];
  /** Set only for `enrichment_low_confidence`. */
  enrichmentProblem: EnrichmentLowConfidenceProblem | null;
  prefill: TinaHitlEventPrefillPayload | null;
  canCreate: boolean;
  canManualClose: boolean;
  reviewReasonRows: Array<{ label: string; value: string }>;
  venueRows: Array<{ label: string; value: string; mono?: boolean }>;
  scoreBreakdown: Record<string, unknown> | null;
  problemJson: string;
}

const EMPTY_DERIVED: DerivedHitlData = {
  musicContext: null,
  cinemaContext: null,
  theaterContext: null,
  sportsContext: null,
  matchedVenue: null,
  matchedArtists: [],
  enrichmentProblem: null,
  prefill: null,
  canCreate: false,
  canManualClose: false,
  reviewReasonRows: [],
  venueRows: [],
  scoreBreakdown: null,
  problemJson: "",
};

function useDerivedHitlData(
  item: PipelineItem,
  hitlContext: HitlContext | null,
): DerivedHitlData {
  return useMemo<DerivedHitlData>(() => {
    if (!hitlContext) return EMPTY_DERIVED;
    const musicContext = readMusicEventContext(item, hitlContext);
    const cinemaContext = readCinemaScreeningContext(item, hitlContext);
    const theaterContext = readTheaterPerformanceContext(item, hitlContext);
    const sportsContext = readSportsMatchContext(item, hitlContext);
    const matchedVenue = readMatchedVenue(item, hitlContext);
    const matchedArtists = readMatchedArtists(item, hitlContext);
    const prefill = buildTinaHitlEventPrefill(item);
    return {
      musicContext,
      cinemaContext,
      theaterContext,
      sportsContext,
      matchedVenue,
      matchedArtists,
      enrichmentProblem: enrichmentProblemOf(hitlContext),
      prefill,
      canCreate:
        canCreateMoodEventFromHitl(item, hitlContext) &&
        Boolean(prefill) &&
        // low_score now owns its create/edit/reject via LowScoreResolution
        // (in-app handoff), so the legacy clipboard CTA is retired for it.
        hitlContext.problem.reason !== "low_score" &&
        // Belt and braces: `canCreateMoodEventFromHitl` reads music-event
        // context and is already false here, but `content_type` was widened to
        // `string`, and that is exactly the kind of change that makes a
        // music-only affordance appear on an enrichment job.
        !isEnrichmentContentType(hitlContext.content_type),
      canManualClose:
        item.status === "hitl" &&
        hitlContext.content_type === "music_event" &&
        hitlContext.problem.reason !== "low_score",
      reviewReasonRows: hitlReviewReasonRows(hitlContext),
      venueRows: venueReviewRows(hitlContext),
      scoreBreakdown: readLowScoreBreakdown(hitlContext),
      problemJson: JSON.stringify(hitlContext.problem, null, 2),
    };
  }, [item, hitlContext]);
}

export function HitlPanel({ item }: { item: PipelineItem }) {
  const ctx = item.hitl_context;
  const [problemOpen, setProblemOpen] = useState(false);
  const derived = useDerivedHitlData(item, ctx);

  if (!ctx) {
    // A coalesced follower deliberately carries no context of its own — the
    // review lives on the representative it was folded into. Don't cry
    // "unexpected"; send the curator to the item that actually holds the
    // resolution UI. (Coalesced followers are hidden from the HITL queue, so a
    // curator only lands here by drilling in from the run view.)
    if (item.coalesced_into_event_id) {
      return <CoalescedReviewNotice item={item} />;
    }
    return (
      <PanelShell status="hitl" subtitle="No HITL context on this item.">
        <p className="text-sm text-muted-foreground">
          The worker emitted a HITL status but did not attach a context payload.
          This is unexpected — investigate the failing stage directly.
        </p>
      </PanelShell>
    );
  }

  const hitlContext = ctx;
  const {
    musicContext,
    cinemaContext,
    theaterContext,
    sportsContext,
    matchedVenue,
    matchedArtists,
    enrichmentProblem,
    prefill,
    canCreate,
    canManualClose,
    reviewReasonRows,
    venueRows,
    scoreBreakdown,
    problemJson,
  } = derived;
  // low_score's own Reject (LowScoreResolution) force-closes; avoid a duplicate
  // header affordance for it. Every other reason keeps the universal escape hatch.
  const canForceClose =
    item.status === "hitl" && hitlContext.problem.reason !== "low_score";

  return (
    <PanelShell
      status="hitl"
      subtitle={hitlSubtitle(hitlContext)}
      headerRight={
        canCreate || canManualClose || canForceClose ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canManualClose && <ManualCloseDialog item={item} />}
            {canCreate && prefill ? (
              <CreateMoodEventButton prefill={prefill} />
            ) : null}
            {canForceClose && <ForceCloseDialog item={item} />}
          </div>
        ) : null
      }
    >
      <HitlContextView
        item={item}
        hitlContext={hitlContext}
        musicContext={musicContext}
        cinemaContext={cinemaContext}
        theaterContext={theaterContext}
        sportsContext={sportsContext}
        matchedArtists={matchedArtists}
        matchedVenue={matchedVenue}
        enrichmentProblem={enrichmentProblem}
      />

      {/* `refresh_review` joins `low_score` in owning its own disclosure. Its
          `reviewReasonRows` is a single generic Trigger sentence — "this event's
          source page now disagrees with what Mood has published" — which the
          mode badge's own hint says better and per mode, above a context view
          that names the event, the model's verdict and the model's reasoning. */}
      {reviewReasonRows.length > 0 &&
        hitlContext.problem.reason !== "low_score" &&
        hitlContext.problem.reason !== "refresh_review" && (
          <HitlReasoningView rows={reviewReasonRows} />
        )}

      {venueRows.length > 0 && <VenueResolutionView rows={venueRows} />}

      {scoreBreakdown && <ScoreBreakdownView breakdown={scoreBreakdown} />}

      {item.status === "hitl" &&
        (() => {
          const Resolution = resolutionComponentFor(hitlContext.problem.reason);
          return (
            <Resolution
              item={item}
              hitlContext={hitlContext}
              onResolved={() => {
                /* registered components navigate via resolveHitl; fallback is a no-op */
              }}
            />
          );
        })()}

      <Separator />

      <Collapsible open={problemOpen} onOpenChange={setProblemOpen}>
        <CollapsibleTrigger className="group inline-flex items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground">
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 transition-transform",
              problemOpen && "rotate-90",
            )}
          />
          {/* The reason used to be spelled out in these brackets, and it was
              the THIRD statement of it on the page: the item header already
              carries a `HitlReasonBadge`, and the subtitle above named it too.
              On a `refresh_review` park a curator read "Refresh — review
              changes", then "reason refresh_review", then "Problem details
              (refresh_review)" — one status, three renderings. This is the raw
              disclosure; what it discloses is enough of a label. */}
          Problem details
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed">
            {problemJson}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </PanelShell>
  );
}

/**
 * The one muted line under "Needs review", and what it is for.
 *
 * `Stuck at <stage> · reason <reason>` is the right line for a scrape park: the
 * stage is where a developer looks next, and the reason is the vocabulary the
 * `hitl_reason` filters use. It is the wrong line for a `refresh_review` park,
 * where it restated a header chip that already reads "Refresh — review changes"
 * and named a stage (`refresh_apply`) that is the same on every refresh park
 * ever raised — so the screen stated its status three times before saying
 * anything a curator could act on (owner ruling R76).
 *
 * A refresh park gets the sentence those two do not carry: what the decision
 * below it decides. The stage is still one glance away in the
 * "Stage executions" sidebar, which is where a developer was reading it from
 * anyway.
 */
function hitlSubtitle(ctx: HitlContext): string {
  if (ctx.problem.reason === "refresh_review") {
    return "Decide what happens to the published Mood event.";
  }
  return `Stuck at ${ctx.stage} · reason ${ctx.problem.reason}`;
}

/**
 * Landing panel for a coalesced follower (status `hitl`, no `hitl_context`, but
 * `coalesced_into_event_id` set). The follower has nothing to resolve on its
 * own — its review was merged into the representative for the same venue — so
 * we explain the grouping and point the curator to that representative.
 */
function CoalescedReviewNotice({ item }: { item: PipelineItem }) {
  const canonicalId = item.coalesced_into_event_id ?? "";
  const target = `/tina/items/show/${canonicalId}`;

  return (
    <PanelShell
      status="hitl"
      title="Grouped review"
      subtitle="Resolved together with other events at the same venue."
    >
      <p className="text-sm text-muted-foreground">
        This event’s review was grouped with other same-venue events under a
        single representative, so it carries no review of its own. Resolving the
        main review clears this one automatically.
      </p>
      <Button asChild size="sm" className="self-start">
        <Link to={target}>
          Open the main review
          <ArrowUpRight className="ml-1 h-4 w-4" />
        </Link>
      </Button>
      <p className="text-xs text-muted-foreground">
        Main item{" "}
        <Link to={target} className="font-mono text-blue-500 hover:underline">
          {shortId(canonicalId)}
        </Link>
      </p>
    </PanelShell>
  );
}

function HitlContextView({
  item,
  hitlContext,
  musicContext,
  cinemaContext,
  theaterContext,
  sportsContext,
  matchedArtists,
  matchedVenue,
  enrichmentProblem,
}: {
  item: PipelineItem;
  hitlContext: HitlContext;
  musicContext: MusicEventContext | null;
  cinemaContext: CinemaScreeningContext | null;
  theaterContext: TheaterPerformanceContext | null;
  sportsContext: SportsMatchContext | null;
  matchedArtists: TextRef[];
  matchedVenue: TextRef | null;
  enrichmentProblem: EnrichmentLowConfidenceProblem | null;
}) {
  // First, because an enrichment job has none of the three scrape contexts and
  // before this branch fell through to "No structured HITL context available".
  if (enrichmentProblem) {
    return <EnrichmentContextView problem={enrichmentProblem} />;
  }
  // And second, for the same reason and with the same history: a
  // `music_event_refresh` job carries no extract/fetch stage outputs either, so
  // every one of the 1,116 parks open when this shipped rendered that same
  // fallback — with the model's own reasoning sitting unread in
  // `stage_outputs.refresh_compare`.
  if (hitlContext.problem.reason === "refresh_review") {
    return <RefreshContextView hitlContext={hitlContext} />;
  }
  // Third, for the same reason again: `enrichment_artist_curation` carries
  // none of the three scrape contexts either, and `CuratorReviewResolution`
  // below already renders the held fields in full — this is only the "which
  // artist is this" line the resolution panel does not otherwise say up top.
  if (hitlContext.problem.reason === "enrichment_curator_review") {
    const problem = hitlContext.problem;
    return (
      <p className="text-sm text-muted-foreground">
        Artist{" "}
        <span className="font-medium text-foreground">
          {problem.artist_name ?? problem.entity_id}
        </span>
        {problem.artist_name && (
          <span className="ml-1 font-mono text-xs">({problem.entity_id})</span>
        )}
      </p>
    );
  }
  if (musicContext) {
    return (
      <MusicContextView
        eventId={item.id}
        context={musicContext}
        matchedArtists={matchedArtists}
        matchedVenue={matchedVenue ?? undefined}
      />
    );
  }
  if (cinemaContext) {
    return (
      <SectionWithHeader title="Screening context">
        <KeyValueGrid rows={cinemaContextRows(cinemaContext, item)} />
      </SectionWithHeader>
    );
  }
  if (theaterContext) {
    return (
      <SectionWithHeader title="Theater context">
        <KeyValueGrid rows={theaterContextRows(theaterContext, item)} />
      </SectionWithHeader>
    );
  }
  if (sportsContext) {
    return (
      <SectionWithHeader title="Match context">
        <KeyValueGrid rows={sportsContextRows(sportsContext)} />
      </SectionWithHeader>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      No structured HITL context available for this content type.
    </p>
  );
}

function SectionWithHeader({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function HitlReasoningView({
  rows,
}: {
  rows: Array<{ label: string; value: string }>;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-3">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Why it needs review
      </h3>
      <KeyValueGrid rows={rows} />
    </div>
  );
}

function VenueResolutionView({
  rows,
}: {
  rows: Array<{ label: string; value: string; mono?: boolean }>;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-3">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Venue resolution
      </h3>
      <KeyValueGrid rows={rows} />
    </div>
  );
}

function CreateMoodEventButton({
  prefill,
}: {
  prefill: TinaHitlEventPrefillPayload;
}) {
  const [isWritingClipboard, setIsWritingClipboard] = useState(false);

  async function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    setIsWritingClipboard(true);
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API is unavailable.");
      }
      await navigator.clipboard.writeText(JSON.stringify(prefill, null, 2));
      window.open(CREATE_HREF, "_blank", "noopener,noreferrer");
    } catch (error) {
      window.alert(toHumanErrorMessage(error));
    } finally {
      setIsWritingClipboard(false);
    }
  }

  return (
    <Button asChild size="sm" disabled={isWritingClipboard}>
      <a
        href={CREATE_HREF}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
      >
        {isWritingClipboard ? (
          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
        ) : (
          <Clipboard className="mr-1 h-4 w-4" />
        )}
        Create Mood event
        <ArrowUpRight className="ml-1 h-4 w-4" />
      </a>
    </Button>
  );
}

function ManualCloseDialog({ item }: { item: PipelineItem }) {
  const { resolveHitl } = useItemsRepository();
  const [open, setOpen] = useState(false);
  const [moodEventId, setMoodEventId] = useState("");
  const [isClosing, setIsClosing] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = moodEventId.trim();
    if (!trimmed) return;

    setIsClosing(true);
    try {
      await resolveHitl(item.id, {
        entity_id: trimmed,
        resolution_kind: "manual_event_created",
      });
      setOpen(false);
      setMoodEventId("");
    } finally {
      setIsClosing(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <CheckCircle2 className="mr-1 h-4 w-4" />
          Close with event id
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Close HITL</DialogTitle>
            <DialogDescription>
              Link this review to an existing Mood event.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={moodEventId}
            onChange={(event) => setMoodEventId(event.target.value)}
            placeholder="Mood event id"
          />
          <DialogFooter>
            <Button type="submit" disabled={!moodEventId.trim() || isClosing}>
              {isClosing && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Close HITL
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ForceCloseDialog({ item }: { item: PipelineItem }) {
  const { resolveHitl, listCoalesced } = useItemsRepository();
  const [open, setOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [instances, setInstances] = useState<PipelineItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // "Give up on this proposal" is a legitimate curator action,
  // so the dialog stays — but its generic copy implies an *event* is being
  // abandoned, which is wrong for an enrichment job.
  const enrichmentProblem = enrichmentProblemOf(item.hitl_context);
  // Force-closing a curator review is a no-op for the artist's data — unlike
  // `enrichmentProblem` above, nothing is left `NeedsReview` here, so it gets
  // its own copy rather than reusing that branch's.
  const curatorReviewProblem = curatorReviewProblemOf(item.hitl_context);
  // Which queue this park came from, if not the shared one. Read here rather than
  // branched on inline so a third own-surface reason cannot land with a third copy
  // of the branch — `refresh_review` was the second.
  const returnTo = hitlQueueReturnTo(item.hitl_context);

  // While this event is in `hitl`, coalesced_count is exactly the set of stuck
  // same-key siblings parked behind this representative. Treat undefined (older
  // API payloads) as zero. Only venue_* and movie_* reasons coalesce, so the
  // group copy is entity-aware (falls back to venue for any unexpected reason).
  //
  // Enrichment is structurally never in that branch: the raiser
  // sets no `hitl_dedup_key`, `_ENRICHMENT_REASONS` is in `hitl.py`'s
  // non-coalescing family set and `coalesce_family_for` returns None, so
  // `coalesced_count` is always 0 and this renders its single-confirm shape.
  const siblingCount = item.coalesced_count ?? 0;
  const hasSiblings = siblingCount > 0;
  const groupCopy = coalesceCopy(item.hitl_reason);
  const groupEntity = groupCopy?.entity ?? "venue";

  // Load the representative + its coalesced waiters when the dialog opens.
  useEffect(() => {
    if (!open || !hasSiblings) return;
    let cancelled = false;
    // Reset before the fetch so the spinner shows and a previous open's cluster
    // doesn't flash while the refetch is in flight.
    setInstances(null);
    setSelected(new Set());
    setLoadError(false);
    void (async () => {
      try {
        const waiters = await listCoalesced(item.id);
        if (cancelled) return;
        // The anchor (this page's event) is selectable alongside its waiters.
        setInstances([item, ...waiters]);
        setSelected(new Set());
      } catch {
        // Surface a terminal error state instead of spinning forever; Cancel
        // stays available so the curator can back out.
        if (cancelled) return;
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Deliberately key on `item.id` only. `useItemsRepository()` returns a new
    // object literal with fresh function refs every render, and `item` is a new
    // prop reference each render too — including `item`/`listCoalesced` here made
    // the effect re-run every render, driving an unbounded listCoalesced() loop
    // that also wiped the curator's ticks each cycle. `listCoalesced` is a
    // stateless fetch wrapper, so the closure captured at render time is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasSiblings, item.id]);

  const nameOf = (row: PipelineItem): string =>
    (
      (row.metadata?.extracted_name as string | undefined) ??
      row.url ??
      row.id
    ).trim();
  const dateOf = (row: PipelineItem): string => {
    const iso = row.metadata?.extracted_start_iso as string | undefined;
    return iso ? new Date(iso).toISOString().slice(0, 10) : "—";
  };
  const groupKey = (row: PipelineItem): string =>
    nameOf(row).toLowerCase().replace(/\s+/g, " ");

  // Stable groups: array of [label, rows] preserving first-seen order.
  const groups: [string, PipelineItem[]][] = [];
  const byKey = new Map<string, PipelineItem[]>();
  for (const row of instances ?? []) {
    const k = groupKey(row);
    if (!byKey.has(k)) {
      const rows: PipelineItem[] = [];
      byKey.set(k, rows);
      groups.push([nameOf(row), rows]);
    }
    byKey.get(k)!.push(row);
  }

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const setMany = useCallback((ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const allIds = (instances ?? []).map((r) => r.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selected.has(id));

  // `close_event_ids` is NOT equivalent to the whole-cluster cascade: in
  // `resolve_hitl` the `close_event_ids` branch returns before the movie
  // `site_movie_cache` suppression write and the key-scoped
  // `terminally_close_coalesced_waiters` boomerang sweep. So when the curator
  // ticks EVERY shown instance we route to `cascade_siblings=true` (the
  // whole-cluster path that preserves suppression + the key-scoped sweep); a
  // partial selection keeps the scoped `close_event_ids` path. The two are
  // mutually exclusive on the wire — the backend validator rejects both.
  async function handleForceClose(extra?: {
    cascade_siblings?: boolean;
    close_event_ids?: string[];
  }) {
    setIsClosing(true);
    try {
      await resolveHitl(
        item.id,
        {
          resolution_kind: "force_closed",
          ...(extra ?? {}),
        },
        // An enrichment or refresh park was never in the shared queue, so
        // returning the curator there would land them somewhere their item
        // has never appeared. `{}` — i.e. `resolveHitl`'s own default — stays
        // the answer for every scrape reason.
        returnTo ? { navigateTo: returnTo } : {},
      );
      setOpen(false);
    } finally {
      setIsClosing(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive">
          <Ban className="mr-1 h-4 w-4" />
          Force close
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Force close HITL</DialogTitle>
          <DialogDescription>
            {hasSiblings ? (
              <>
                This {groupEntity} has {siblingCount} other unresolved event
                {siblingCount === 1 ? "" : "s"} queued behind this one. Select
                the instances to close — the rest stay in the queue.
              </>
            ) : enrichmentProblem ? (
              <>
                Closes this job without applying the proposal.{" "}
                {enrichmentEntityLabel(enrichmentProblem.entity_type)}{" "}
                <code className="font-mono text-xs">
                  {enrichmentProblem.entity_id}
                </code>{" "}
                stays <strong>NeedsReview</strong> and nothing is written to its
                canonical fields.
              </>
            ) : curatorReviewProblem ? (
              <>
                The curator&rsquo;s values stay exactly as they are — nothing is
                written, and nothing is recorded as declined. The next automatic
                enrichment write that disagrees with one of these fields will
                park a new review and ask again.
              </>
            ) : (
              <>
                Mark this Tina item done without linking or creating a Mood
                entity.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {hasSiblings ? (
          <div className="flex flex-col gap-3">
            {loadError ? (
              <p className="text-sm text-destructive">
                Failed to load instances.
              </p>
            ) : instances === null ? (
              <p className="text-sm text-muted-foreground">
                <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading
                instances…
              </p>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Checkbox
                    aria-label="Select all"
                    checked={allSelected}
                    onCheckedChange={(value) => setMany(allIds, value === true)}
                  />
                  Select all ({allIds.length})
                </label>
                <div className="max-h-72 overflow-y-auto rounded-md border">
                  {groups.map(([label, rows]) => {
                    const ids = rows.map((r) => r.id);
                    const groupOn = ids.every((id) => selected.has(id));
                    return (
                      <div key={label} className="border-b p-2 last:border-b-0">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <Checkbox
                            aria-label={label}
                            checked={groupOn}
                            onCheckedChange={(value) =>
                              setMany(ids, value === true)
                            }
                          />
                          {label} ({rows.length})
                        </label>
                        <div className="ml-6 mt-1 flex flex-col gap-1">
                          {rows.map((r) => (
                            // `min-w-0` on both the row and the URL span is what
                            // keeps a long percent-encoded source URL inside the
                            // dialog: a flex item defaults to `min-width:auto`,
                            // so without it the unbreakable URL sets the row's
                            // min-content width and `truncate` never kicks in.
                            <label
                              key={r.id}
                              className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground"
                            >
                              <Checkbox
                                aria-label={`${nameOf(r)} ${dateOf(r)}`}
                                checked={selected.has(r.id)}
                                onCheckedChange={() => toggle(r.id)}
                              />
                              <span className="shrink-0 whitespace-nowrap">
                                {dateOf(r)}
                              </span>
                              {r.url && (
                                <span
                                  className="min-w-0 flex-1 truncate opacity-70"
                                  title={r.url}
                                >
                                  {r.url}
                                </span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isClosing}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                disabled={isClosing || selected.size === 0}
                onClick={() =>
                  void handleForceClose(
                    allSelected
                      ? { cascade_siblings: true }
                      : { close_event_ids: [...selected] },
                  )
                }
              >
                {isClosing && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                <Ban className="mr-1 h-4 w-4" />
                {allSelected
                  ? `Force-close all ${allIds.length} at this ${groupEntity}`
                  : `Force-close selected (${selected.size})`}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isClosing}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleForceClose()}
              disabled={isClosing}
            >
              {isClosing && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Force close HITL
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MusicContextView({
  eventId,
  context,
  matchedArtists,
  matchedVenue,
}: {
  eventId: string;
  context: MusicEventContext;
  matchedArtists: TextRef[];
  matchedVenue?: TextRef;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row-reverse sm:items-start sm:gap-4">
        {(context.selectedImage?.ref || context.selectedImage?.url) && (
          <figure className="flex max-w-md shrink-0 flex-col gap-2 sm:w-48 sm:max-w-none">
            <GcsImage
              eventId={eventId}
              artifactRef={context.selectedImage.ref}
              fallbackUrl={context.selectedImage.url}
              alt="Selected event image"
              className="max-h-64 w-full rounded-md border object-cover"
            />
            <figcaption className="text-xs text-muted-foreground">
              {imageCaption(context.selectedImage)}
            </figcaption>
          </figure>
        )}
        <div className="min-w-0 flex-1">
          <SectionWithHeader title="Proposed event">
            <KeyValueGrid
              rows={musicContextRows(context, matchedVenue, matchedArtists)}
            />
          </SectionWithHeader>
        </div>
      </div>
    </div>
  );
}

function imageCaption(image: MusicEventContext["selectedImage"]): string {
  const parts = [image?.classification, image?.source]
    .map((part) => part?.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Selected image";
}

function musicContextRows(
  context: MusicEventContext,
  matchedVenue: TextRef | undefined,
  matchedArtists: TextRef[],
) {
  return [
    { label: "Name", value: context.name ?? "—" },
    { label: "Start date", value: context.startDate ?? "—" },
    { label: "Timezone", value: context.timezone ?? "—" },
    { label: "Type", value: context.type ?? "—" },
    { label: "Venue", value: context.venueName ?? "—" },
    {
      label: "Venue id",
      value: matchedVenue?.id ?? "—",
      mono: Boolean(matchedVenue?.id),
    },
    { label: "City", value: context.cityName ?? "—" },
    {
      label: "Lineup",
      value:
        context.lineupNames.length > 0 ? context.lineupNames.join(", ") : "—",
    },
    {
      label: "Artist ids",
      value: formatRefs(matchedArtists),
      mono: matchedArtists.length > 0,
    },
    {
      label: "Genres",
      value:
        context.genreNames.length > 0 ? context.genreNames.join(", ") : "—",
    },
  ];
}

function cinemaContextRows(
  context: CinemaScreeningContext,
  item: PipelineItem,
) {
  return [
    { label: "Movie", value: context.movieTitle ?? "—" },
    {
      label: "Resolved",
      value: context.resolvedMovieTitle ?? context.movieId ?? "—",
    },
    { label: "Movie ID", value: context.movieId ?? "—", mono: true },
    { label: "TMDb", value: context.tmdbId ?? "—", mono: true },
    { label: "Screening", value: context.screeningStartsAtLocal ?? "—" },
    { label: "Venue", value: context.venueName ?? "—" },
    {
      label: "Source",
      value: context.sourceUrl ? (
        <a
          href={context.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 hover:underline"
        >
          {context.sourceMovieId ?? context.sourceUrl}
        </a>
      ) : (
        "—"
      ),
    },
    {
      label: "Parent item",
      value: item.parent_event_id ? (
        <a
          href={`/tina/items/show/${item.parent_event_id}`}
          className="font-mono text-blue-500 hover:underline"
        >
          {shortId(item.parent_event_id)}
        </a>
      ) : (
        "—"
      ),
      mono: true,
    },
  ];
}

/** "Sun 11 Oct 2026, 21:00 (Europe/Athens)" — in the match's own zone, not the curator's. */
export function formatKickoff(context: SportsMatchContext): string {
  if (!context.kickoff) return "—";
  const date = new Date(context.kickoff);
  if (Number.isNaN(date.getTime())) return context.kickoff;
  const formatted = new Intl.DateTimeFormat("en-GB", {
    timeZone: context.timezone,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(context.timeConfirmed ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
  const zone = context.timezone ? ` (${context.timezone})` : "";
  return context.timeConfirmed
    ? `${formatted}${zone}`
    : `${formatted} — time TBC`;
}

function sportsContextRows(context: SportsMatchContext) {
  const league = [context.league, context.round].filter(Boolean).join(" · ");
  return [
    {
      label: "Match",
      value: `${context.homeTeam ?? "?"} vs ${context.awayTeam ?? "?"}`,
    },
    { label: "Home team", value: context.homeTeam ?? "—" },
    { label: "League", value: league || "—" },
    { label: "Sport", value: context.sport ?? "—" },
    { label: "Kickoff", value: formatKickoff(context) },
    {
      label: "Verify",
      value: context.sourceUrl ? (
        <a
          href={context.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 hover:underline"
        >
          Search this match
        </a>
      ) : (
        "—"
      ),
    },
  ];
}

function theaterContextRows(
  context: TheaterPerformanceContext,
  item: PipelineItem,
) {
  return [
    { label: "Play", value: context.playTitle ?? "—" },
    { label: "Original", value: context.originalTitle ?? "—" },
    { label: "Target", value: context.targetPersonName ?? "—" },
    { label: "Performance", value: context.startsAtLocal ?? "—" },
    { label: "Venue", value: context.venueName ?? "—" },
    { label: "Candidates", value: context.candidateCount ?? "—" },
    { label: "Searches", value: context.searchAttemptCount ?? "—" },
    { label: "Notes", value: context.notes ?? "—" },
    {
      label: "Source",
      value: context.sourceUrl ? (
        <a
          href={context.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 hover:underline"
        >
          {context.sourceUrl}
        </a>
      ) : (
        "—"
      ),
    },
    {
      label: "Parent item",
      value: item.parent_event_id ? (
        <a
          href={`/tina/items/show/${item.parent_event_id}`}
          className="font-mono text-blue-500 hover:underline"
        >
          {shortId(item.parent_event_id)}
        </a>
      ) : (
        "—"
      ),
      mono: true,
    },
  ];
}

function formatRefs(refs: TextRef[]) {
  if (refs.length === 0) return "—";
  return refs
    .map((ref) => (ref.id ? `${ref.name ?? ref.id} (${ref.id})` : ref.name))
    .filter(Boolean)
    .join(", ");
}

function hitlReviewReasonRows(ctx: HitlContext) {
  const problem = ctx.problem;
  const rows: Array<{ label: string; value: string }> = [];
  const trigger = hitlTriggerText(problem.reason);
  if (trigger) {
    rows.push({ label: "Trigger", value: trigger });
  }

  // `gemini_*` is the scrape stages' spelling; the enrichment gate writes plain
  // `reasoning`/`confidence`/`threshold`. Reading both keeps
  // this grid populated for every reason instead of showing three blanks — and
  // the fallback ordering means a problem carrying both never doubles a row.
  const geminiReasoning =
    problemText(problem, "gemini_reasoning") ??
    problemText(problem, "reasoning");
  if (geminiReasoning) {
    rows.push({ label: "Gemini", value: geminiReasoning });
  }

  const geminiConfidence =
    problemNumber(problem, "gemini_confidence") ??
    problemNumber(problem, "confidence");
  if (geminiConfidence !== null) {
    const threshold = problemNumber(problem, "threshold");
    rows.push({
      label: "Confidence",
      value:
        threshold === null
          ? `${Math.round(geminiConfidence * 100)}%`
          : `${Math.round(geminiConfidence * 100)}% (gate ${Math.round(threshold * 100)}%)`,
    });
  }

  const reviewReason = problemText(problem, "review_reason");
  if (reviewReason) {
    rows.push({ label: "Review", value: reviewReason });
  }

  const notes = problemText(problem, "notes");
  if (notes) {
    rows.push({ label: "Notes", value: notes });
  }

  return rows;
}

function venueReviewRows(ctx: HitlContext) {
  if (!ctx.problem.reason.startsWith("venue_")) return [];
  const problem = ctx.problem;
  const resolverStats = asRecord(problemField(problem, "resolver_stats"));
  const selectedCandidate = asRecord(
    problemField(problem, "selected_candidate"),
  );
  const geodataCandidate = asRecord(problemField(problem, "geodata_candidate"));
  const rows: Array<{ label: string; value: string; mono?: boolean }> = [];

  pushTextRow(rows, "Extracted", problemField(problem, "extracted_venue_name"));
  pushTextRow(
    rows,
    "Original",
    problemField(problem, "extracted_venue_name_original"),
  );
  pushTextRow(
    rows,
    "Address",
    problemField(problem, "extracted_venue_address"),
  );
  pushTextRow(
    rows,
    "Search",
    problemField(problem, "normalized_search_query"),
    true,
  );
  pushTextRow(rows, "Candidates", problemField(problem, "candidate_count"));
  pushTextRow(rows, "Selected", candidateLabel(selectedCandidate));
  pushTextRow(rows, "Geodata", candidateLabel(geodataCandidate));
  pushTextRow(rows, "Rejection", problemField(problem, "rejection_reason"));
  pushTextRow(rows, "Places calls", resolverStats.places_calls);
  pushTextRow(rows, "Capped", resolverStats.places_candidates_capped);

  return rows;
}

function readLowScoreBreakdown(ctx: HitlContext) {
  if (ctx.problem.reason !== "low_score") return null;
  return asRecord(problemField(ctx.problem, "score_breakdown"));
}

function pushTextRow(
  rows: Array<{ label: string; value: string; mono?: boolean }>,
  label: string,
  value: unknown,
  mono = false,
) {
  const text = formatUnknownDisplay(value);
  if (text === "—") return;
  rows.push({ label, value: text, mono });
}

function candidateLabel(candidate: Record<string, unknown>) {
  if (Object.keys(candidate).length === 0) return "—";
  const name = formatUnknownDisplay(candidate.name);
  const address = formatUnknownDisplay(candidate.address);
  const cid = formatUnknownDisplay(candidate.cid);
  return [name, address, cid !== "—" ? `cid ${cid}` : null]
    .filter(Boolean)
    .join(" · ");
}

function hitlTriggerText(reason: HitlContext["problem"]["reason"]) {
  switch (reason) {
    case "venue_no_address":
      return "The extracted event did not include a venue address.";
    case "venue_places_miss":
      return "Google Places did not return a usable venue candidate.";
    case "venue_low_confidence":
      return "Gemini did not choose a venue candidate confidently enough.";
    case "venue_geodata_miss":
      return "The selected venue could not be mapped to geodata during auto-create.";
    case "lineup_too_ambiguous":
      return "The extracted lineup had too many ambiguous artist matches.";
    case "artist_ambiguous_match":
      return "Artist resolution found ambiguous Spotify matches.";
    case "artist_spotify_miss":
      return "Artist resolution could not find required Spotify matches.";
    case "artist_spotify_no_image":
      return "Artist resolution found Spotify matches without usable images.";
    case "artist_low_confidence":
      return "Artist resolution confidence fell below the review threshold.";
    case "movie_tmdb_miss":
      return "Movie resolution could not find a TMDb match.";
    case "movie_low_confidence":
      return "Movie resolution confidence fell below the review threshold.";
    case "movie_ambiguous_match":
      return "Movie resolution found multiple plausible matches.";
    case "play_ambiguous_match":
      return "Play resolution found multiple plausible matches.";
    case "play_no_image_found":
      return "Play resolution could not find a usable image.";
    case "person_ambiguous_match":
      return "Person resolution found multiple plausible matches.";
    case "low_score":
      return "The final score fell below the auto-approval threshold.";
    case "image_low_confidence":
      return "The selected event image was flagged as a possible mismatch for this event.";
    case "enrichment_low_confidence":
      return "The enrichment model's self-reported confidence fell below this entity's threshold.";
    case "enrichment_curator_review":
      return "An automatic enrichment write disagreed with a field a curator already edited.";
    case "refresh_review":
      return "This event's source page now disagrees with what Mood has published, or has gone away.";
    default: {
      const _exhaustive: never = reason;
      return _exhaustive;
    }
  }
}

function problemText(problem: HitlProblem, key: string) {
  const value = problemField(problem, key);
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function problemNumber(problem: HitlProblem, key: string) {
  const value = problemField(problem, key);
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function problemField(problem: HitlProblem, key: string): unknown {
  return (problem as unknown as Record<string, unknown>)[key];
}

function formatUnknownDisplay(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || "—";
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "yes" : "no";
  return "—";
}
